import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel,
  Avatar,
  Divider,
  IconButton
} from '@mui/material';
import { MdDelete } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { createCombo, updateCombo, getComboById } from '../../Webservice/offerAPI';
import { getData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import { API_URL } from '../../Utils/url';
import Swal from 'sweetalert2';

const getUserId = () => {
    let userId = null;
    ['user', 'adminUser', 'commonUser', 'sellerUser'].forEach(key => {
        if (userId) return;
        const u = sessionStorage.getItem(key) || localStorage.getItem(key);
        if (u) {
            try {
                const parsed = JSON.parse(u);
                userId = parsed._id || parsed.id || null;
            } catch (e) {}
        }
    });
    return userId || sessionStorage.getItem('userId') || localStorage.getItem('userId') || localStorage.getItem('commonUserId') || '';
};

const steps = ['Basic Details', 'Select Products', 'Pricing & Status', 'Review & Submit'];

function CreateComboOffer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [activeStep, setActiveStep] = useState(0);
  
  // Data for Selectors
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  
  // State for Form Data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    comboCode: '',
    visibility: ['b2b', 'b2c'],
    products: [], // Array of objects { product: {...}, quantity: 1 }
    b2c_mrp: 0,
    b2c_sellingPrice: '',
    b2b_mrp: 0,
    b2b_sellingPrice: '',
    isActive: true,
    image: null,
    imagePreview: ''
  });

  const [loading, setLoading] = useState(false);

  // Initial Category Fetch
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const catRes = await getData('categories/list?limit=1000', null, token);
        if (catRes && catRes.categories) {
            setCategories(Array.isArray(catRes.categories) ? catRes.categories : []);
        }
      } catch (error) {
        console.error('Failed to fetch categories', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch Subcategories when Category changes
  useEffect(() => {
    const fetchSubcategories = async () => {
      if (!selectedCategory) {
          setSubcategories([]);
          return;
      }
      try {
          const tokenValue = sessionStorage.getItem('token');
          const token = tokenValue ? `Bearer ${tokenValue}` : '';
          const response = await getData(`subcategories/list?limit=1000&category_id=${selectedCategory}`, null, token);
          setSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
      } catch (error) {
          setSubcategories([]);
      }
    };
    fetchSubcategories();
  }, [selectedCategory]);

  // Fetch all products for selection
  useEffect(() => {
    const fetchProducts = async () => {
      try {
          const tokenValue = sessionStorage.getItem('token');
          const token = tokenValue ? `Bearer ${tokenValue}` : '';
          let queryParams = new URLSearchParams();
          queryParams.append('limit', '1000');
          if (selectedCategory) queryParams.append('categoryId', selectedCategory);
          if (selectedSubcategory) queryParams.append('subcategoryId', selectedSubcategory);
          
          const response = await getData(`${PRODUCT_ROUTES.GET_PRODUCT}?${queryParams.toString()}`, null, token);
          setAllProducts(Array.isArray(response.products) ? response.products : []);
      } catch (error) {
          console.error('Failed to fetch products', error);
      }
    };
    fetchProducts();
  }, [selectedCategory, selectedSubcategory]);

  // Fetch Combo for editing
  useEffect(() => {
    if (id) {
        const fetchEditData = async () => {
            try {
                const response = await getComboById(id);
                const combo = response.data || response.combo;
                if (combo) {
                    const mappedProducts = combo.products?.map(p => ({
                        product: p.product_id, // Already populated by API
                        quantity: p.quantity || 1
                    })) || [];
                    
                    setFormData({
                        title: combo.title || '',
                        description: combo.description || '',
                        comboCode: combo.comboCode || '',
                        visibility: combo.visibility || ['b2b', 'b2c'],
                        products: mappedProducts,
                        b2c_mrp: combo.b2c_mrp || 0,
                        b2c_sellingPrice: combo.b2c_sellingPrice || '',
                        b2b_mrp: combo.b2b_mrp || 0,
                        b2b_sellingPrice: combo.b2b_sellingPrice || '',
                        isActive: combo.isActive !== undefined ? combo.isActive : true,
                        image: null,
                        imagePreview: combo.product_images?.[0] || ''
                    });
                }
            } catch (error) {
                console.error('Failed to fetch Combo for editing', error);
                Swal.fire('Error', 'Failed to load combo details', 'error');
            }
        };
        fetchEditData();
    }
  }, [id]);

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
          setFormData(prev => ({
              ...prev,
              image: file,
              imagePreview: URL.createObjectURL(file)
          }));
      }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'isActive') {
        setFormData(prev => ({ ...prev, isActive: checked }));
        return;
    }
    
    setFormData((prev) => {
      let vis = [...prev.visibility];
      if (checked) {
        vis.push(name);
      } else {
        vis = vis.filter((v) => v !== name);
      }
      return { ...prev, visibility: vis };
    });
  };

  const handleAddProducts = (newProducts) => {
      // Find what was added and removed
      const currentIds = formData.products.map(p => p.product._id);
      const newIds = newProducts.map(p => p._id);
      
      const resultingProducts = [];
      
      // Keep existing ones with their quantities
      formData.products.forEach(p => {
          if (newIds.includes(p.product._id)) {
              resultingProducts.push(p);
          }
      });
      
      // Add new ones with default quantity 1
      newProducts.forEach(p => {
          if (!currentIds.includes(p._id)) {
              resultingProducts.push({ product: p, quantity: 1 });
          }
      });
      
      // Auto calculate MRP (same for both B2C and B2B)
      const calcMrp = resultingProducts.reduce((acc, item) => acc + ((item.product.b2c_mrp || 0) * item.quantity), 0);
      
      setFormData(prev => ({ ...prev, products: resultingProducts, b2c_mrp: calcMrp, b2b_mrp: calcMrp }));
  };

  const handleQuantityChange = (productId, newQuantity) => {
      const q = parseInt(newQuantity, 10);
      if (isNaN(q) || q < 1) return;
      
      setFormData(prev => {
          const updatedProducts = prev.products.map(p => 
              p.product._id === productId ? { ...p, quantity: q } : p
          );
          
          const calcMrp = updatedProducts.reduce((acc, item) => acc + ((item.product.b2c_mrp || 0) * item.quantity), 0);
          
          return { ...prev, products: updatedProducts, b2c_mrp: calcMrp, b2b_mrp: calcMrp };
      });
  };

  const removeProduct = (productId) => {
      setFormData(prev => {
          const updatedProducts = prev.products.filter(p => p.product._id !== productId);
          const calcMrp = updatedProducts.reduce((acc, item) => acc + ((item.product.b2c_mrp || 0) * item.quantity), 0);
          
          return { ...prev, products: updatedProducts, b2c_mrp: calcMrp, b2b_mrp: calcMrp };
      });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const targetProducts = formData.products.map(item => ({
        product_id: item.product._id,
        quantity: item.quantity
      }));

      const userId = getUserId();
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('comboCode', formData.comboCode);
      submitData.append('visibility', JSON.stringify(formData.visibility));
      submitData.append('products', JSON.stringify(targetProducts));
      submitData.append('b2c_mrp', formData.b2c_mrp);
      submitData.append('b2c_sellingPrice', formData.b2c_sellingPrice);
      submitData.append('b2b_mrp', formData.b2b_mrp);
      submitData.append('b2b_sellingPrice', formData.b2b_sellingPrice);
      submitData.append('isActive', formData.isActive);
      submitData.append('createdBy', userId);
      submitData.append('action', isEdit ? 'Update' : 'Create');
      submitData.append('entity', 'Combo Offer');

      if (formData.image) {
          submitData.append('product_images', formData.image);
      }

      const res = isEdit ? await updateCombo(id, submitData) : await createCombo(submitData);
      if (res && res.success) {
        Swal.fire({
          title: 'Success',
          text: `Combo Offer ${isEdit ? 'Updated' : 'Created'} Successfully`,
          icon: 'success'
        });
        navigate('/offers/combo');
      } else {
        throw new Error(res?.message || `Failed to ${isEdit ? 'update' : 'create'} Combo offer`);
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Something went wrong',
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Combo Title" name="title" value={formData.title} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Combo Code (SKU)" name="comboCode" value={formData.comboCode} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" name="description" value={formData.description} onChange={handleChange} multiline rows={3} />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Visibility</FormLabel>
                <FormGroup row>
                  <FormControlLabel control={<Checkbox checked={formData.visibility.includes('b2b')} onChange={handleCheckboxChange} name="b2b" />} label="B2B" />
                  <FormControlLabel control={<Checkbox checked={formData.visibility.includes('b2c')} onChange={handleCheckboxChange} name="b2c" />} label="B2C" />
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" mb={1}>Upload Combo Image</Typography>
                <Button variant="outlined" component="label">
                  Choose Image
                  <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                </Button>
                {formData.imagePreview && (
                  <Box sx={{ mt: 2, width: 150, height: 150, border: '1px solid #ccc', borderRadius: 2, overflow: 'hidden' }}>
                    <img src={formData.imagePreview.startsWith('blob') || formData.imagePreview.startsWith('http') ? formData.imagePreview : `${API_URL.replace(/\/api\/v1\/?$/, '')}${formData.imagePreview}`} alt="Combo Preview" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Filter by Category</InputLabel>
                <Select value={selectedCategory} label="Filter by Category" onChange={(e) => setSelectedCategory(e.target.value)}>
                  <MenuItem value=""><em>All Categories</em></MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat._id} value={cat._id}>{cat.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!selectedCategory}>
                <InputLabel>Filter by Subcategory</InputLabel>
                <Select value={selectedSubcategory} label="Filter by Subcategory" onChange={(e) => setSelectedSubcategory(e.target.value)}>
                  <MenuItem value=""><em>All Subcategories</em></MenuItem>
                  {subcategories.map(sub => (
                    <MenuItem key={sub._id} value={sub._id}>{sub.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={allProducts}
                getOptionLabel={(option) => option.title || ''}
                value={formData.products.map(p => p.product)}
                onChange={(event, newValue) => {
                  handleAddProducts(newValue);
                }}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Products"
                    placeholder="Search Products..."
                    required={formData.products.length === 0}
                  />
                )}
              />
            </Grid>
            
            {formData.products.length > 0 && (
                <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight={700} mb={2}>Selected Products Quantities:</Typography>
                    {formData.products.map((item, idx) => (
                        <Paper key={item.product._id} elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                                src={item.product.product_images?.[0] ? (item.product.product_images[0].startsWith('http') ? item.product.product_images[0] : `${API_URL.replace(/\/api\/v1\/?$/, '')}${item.product.product_images[0]}`) : ''} 
                                variant="rounded" 
                                sx={{ width: 50, height: 50, mr: 2 }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography fontWeight={700}>{item.product.title}</Typography>
                                <Typography variant="caption" color="text.secondary">MRP: ₹{item.product.b2c_mrp || 0}</Typography>
                            </Box>
                            <Box sx={{ width: '120px', display: 'flex', alignItems: 'center' }}>
                                <TextField
                                    type="number"
                                    size="small"
                                    label="Qty"
                                    value={item.quantity}
                                    onChange={(e) => handleQuantityChange(item.product._id, e.target.value)}
                                    inputProps={{ min: 1 }}
                                />
                            </Box>
                            <IconButton onClick={() => removeProduct(item.product._id)} sx={{ color: '#ef4444', ml: 2 }}>
                                <MdDelete />
                            </IconButton>
                        </Paper>
                    ))}
                </Grid>
            )}
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    The Total MRP is auto-calculated based on the sum of the selected products' MRPs and their quantities.
                </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Total MRP" name="b2c_mrp" value={formData.b2c_mrp} onChange={handleChange} required InputProps={{ readOnly: true }} sx={{ backgroundColor: '#f1f5f9' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Combo Selling Price (B2C)" name="b2c_sellingPrice" value={formData.b2c_sellingPrice} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Combo Selling Price (B2B)" name="b2b_sellingPrice" value={formData.b2b_sellingPrice} onChange={handleChange} />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={formData.isActive} onChange={handleCheckboxChange} name="isActive" color="primary" />}
                label="Set Combo as Active"
              />
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Box>
            <Typography variant="h6" mb={2}>Review Combo Offer</Typography>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f1f5f9', mb: 2 }}>
                <Typography><b>Title:</b> {formData.title}</Typography>
                <Typography><b>Code:</b> {formData.comboCode}</Typography>
                <Typography><b>Visibility:</b> {formData.visibility.join(', ').toUpperCase()}</Typography>
                <Typography><b>Status:</b> {formData.isActive ? 'Active' : 'Inactive'}</Typography>
            </Paper>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#e0e7ff', mb: 2 }}>
                <Typography fontWeight={700} mb={1}>Products Included:</Typography>
                {formData.products.map(p => (
                    <Typography key={p.product._id} variant="body2" sx={{ ml: 2 }}>
                        • {p.quantity} x {p.product.title}
                    </Typography>
                ))}
            </Paper>
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#dcfce7', mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography><b>Total MRP:</b> ₹{formData.b2c_mrp}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="success.main" fontWeight={700}><b>Selling Price:</b> ₹{formData.b2c_sellingPrice}</Typography>
                    </Grid>
                </Grid>
            </Paper>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box p={3} sx={{ backgroundColor: '#f8fafc', minHeight: '80vh', borderRadius: '10px' }}>
      <Typography variant="h4" mb={4} fontWeight={800} fontFamily="Mulish, sans-serif">
        {isEdit ? 'Edit Combo Offer' : 'Create Combo Offer'}
      </Typography>
      
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 5 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: '300px' }}>
          {renderStepContent(activeStep)}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          >
            Back
          </Button>
          
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading || formData.products.length === 0}
              sx={{ fontWeight: 700, backgroundColor: '#0d47a1', color: '#fff' }}
            >
              {loading ? 'Submitting...' : (isEdit ? 'Update Combo' : 'Create Combo')}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                  (activeStep === 0 && (!formData.title || !formData.comboCode)) ||
                  (activeStep === 1 && formData.products.length === 0)
              }
              sx={{ fontWeight: 700, backgroundColor: 'rgb(255, 209, 2)', color: 'black' }}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default CreateComboOffer;
